import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input, OnDestroy,
  OnInit,
  Output, QueryList,
  Renderer2,
  ViewChildren
} from '@angular/core';
import {HttpErrorResponse} from "@angular/common/http";
import {animationFrameScheduler, filter, startWith, Subscription, take} from "rxjs";

import {GetRegion2Css, GetRegion2Name} from '../../defines/client.const';
import {GetHanjaHtml} from '../../defines/client.func';
import {GetSaseungTxt} from '../../defines/gukeo.func';
import {SimpleWord} from "../../models/simple-word.model";
import {GlobalEventService} from "../../services/global-event/global-event.service";
import {clickOutsideLinkMixin} from "../../mixins/click-outside.mixin";
import {HanjaService, HanjaWordInfo} from "../../services/hanja/hanja.service";


const MAX_WORD_ITEM = 4;
const POPUP_HEIGHT_DELTA = 60;


@Component({
  selector: 'app-hanja-word-popup',
  templateUrl: './hanja-word-popup.component.html',
  styleUrls: ['./hanja-word-popup.component.scss']
})
export class HanjaWordPopupComponent extends clickOutsideLinkMixin implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class') hostClass = 'link-popup popup-connect type-shadow';
  @ViewChildren('wordEl', { read: ElementRef }) wordElements!: QueryList<ElementRef>;
  @Input() hanja?: string;
  @Input() hangul?: string;    // 음 (1글자, 예: "국")
  @Input() firstHun?: string;  // 대표 훈 (예: "나라")
  @Output() close = new EventEmitter<string | null>();
  @Output() viewInit = new EventEmitter<HTMLElement>();

  protected wordColumnMinWidth: number | null = null;

  protected readonly GetRegion2Css = GetRegion2Css;
  protected readonly GetRegion2Name = GetRegion2Name;
  protected readonly GetSaseungTxt = GetSaseungTxt;
  protected readonly GetHanjaHtml = GetHanjaHtml;

  public errorTxt: string | null = null;

  protected countPopupItems = MAX_WORD_ITEM;
  private load$: Subscription | null = null;
  private wordWidthSub: Subscription | null = null;
  private destroyed = false;
  public hanjaWordInfo: HanjaWordInfo | null = null;

  public get listWord(): Array<SimpleWord> | null {
    return this.hanjaWordInfo?.words.slice(0, MAX_WORD_ITEM) ?? null;
  }

  private get totalWordCount(): number | null {
    return this.hanjaWordInfo?.word_total_count ?? null;
  }

  public get IsLoading(): boolean {
    return this.load$ !== null && !this.errorTxt;
  }

  constructor(
    public elementRef: ElementRef,
    public renderer: Renderer2,
    public globalEventSvc: GlobalEventService,
    private cdRef: ChangeDetectorRef,
    private hanjaSvc: HanjaService,
  ) {
    super(elementRef, renderer, globalEventSvc);

    console.warn('HanjaWordPopupComponent.constructor()');
  }

  ngOnInit(): void {
    // mixin을 이용해서 팝업창 외부를 클릭했는지 체크한다.
    this.coInit(() => this.close.emit(null));

    if (this.hanja) {
      this.load$ = this.hanjaSvc.GetHanjaWordInfo(this.hanja, this.hangul).subscribe(
        (hanjaWordInfo: HanjaWordInfo) => {
          console.warn('HanjaWordPopupComponent.ngOnInit: success', {
            hanja: this.hanja,
            wordCount: hanjaWordInfo.word_total_count
          });
          this.hanjaWordInfo = hanjaWordInfo;
        },
        (error) => {
          this.load$ = null;

          console.error('HanjaWordPopupComponent.ngOnInit: error loading hanja info', {
            hanja: this.hanja,
            hangul: this.hangul,
            error,
            status: error?.status,
            statusText: error?.statusText,
            message: error?.message,
            detail: error?.error?.detail
          });

          if (error instanceof HttpErrorResponse) {
            if (error.status === 404) {
              this.errorTxt = '문자 정보를 찾을 수 없습니다.';
            } else if (error.status === 0) {
              this.errorTxt = '네트워크 연결을 확인해 주세요.';
            } else if (error.status >= 400 && error.status < 500) {
              this.errorTxt = '요청한 정보를 처리할 수 없습니다. 다시 시도해 주세요.';
            } else if (error.status >= 500) {
              this.errorTxt = '서버에 일시적인 문제가 있습니다. 잠시 후 다시 시도해 주세요.';
            } else {
              this.errorTxt = '정보를 불러오는 중 오류가 발생했습니다.';
            }
          } else {
            console.error('HanjaWordPopupComponent.ngOnInit: Non-HTTP error', error);
            this.errorTxt = '데이터 처리 중 오류가 발생했습니다.';
          }
        },
        () => {
          this.load$ = null;
        }
      );

    }
  }

  ngAfterViewInit(): void {
    this.wordWidthSub = this.wordElements.changes.pipe(
      startWith(this.wordElements),
      filter((els: QueryList<ElementRef>) => els.length > 0),
      take(1)
    ).subscribe(els => {
      setTimeout(() => {
        if (this.destroyed) return;
        const maxWidth = Math.max(...els.map((el: ElementRef) => el.nativeElement.offsetWidth));
        if (maxWidth > 0) {
          this.wordColumnMinWidth = maxWidth;
          this.cdRef.detectChanges();
        } else {
          console.warn('HanjaWordPopupComponent: word 요소의 maxWidth가 0이므로 CSS min-width로 대체합니다.');
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.coDestroy();

    // 로딩 하고 있으면, 로딩을 중단한다.
    if (this.load$) {
      this.load$.unsubscribe();
      this.load$ = null;
    }
    if (this.wordWidthSub) {
      this.wordWidthSub.unsubscribe();
      this.wordWidthSub = null;
    }
  }

  public onCloseMore($event: MouseEvent): void {
    console.warn('HanjaWordPopupComponent.onCloseMore');

    $event.stopPropagation();
    $event.preventDefault();

    this.close.emit(this.hanja);
  }

  protected getMoreTxt(): string | null {
    let moreTxt: string | null = null;
    const leftCount = (this.totalWordCount ?? 0) - (this.listWord?.length ?? 0);
    moreTxt = leftCount ? `${leftCount}개의 단어가 더 있습니다.` : null;
    return moreTxt;
  }

  public getLeftWordCount(): number {
    return (this.totalWordCount ?? 0) - this.countPopupItems;
  }

  /**
   * 상위 요소로 이벤트 전파를 막는다.
   * @param event
   */
  onVoid(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // this.globalEventSvc.Click(event);
  }

  onItemAfterViewInit() {
    const componentHeight = this.elementRef.nativeElement.offsetHeight;
    const windowHeight = window.innerHeight;

    console.log('HanjaWordPopupComponent.onItemAfterViewInit', componentHeight, windowHeight, this.elementRef.nativeElement);

    if (componentHeight > windowHeight - POPUP_HEIGHT_DELTA) {
      console.warn('remove display', componentHeight, windowHeight - POPUP_HEIGHT_DELTA);
      animationFrameScheduler.schedule(() => {
        --this.countPopupItems;
        this.cdRef.detectChanges();
      });
    }
  }

  onClose($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.close.emit(null);
  }

  protected GetMeaning(word: SimpleWord) {
    return word.GetBriefMeaning() ?? '';
  }
}
